import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";

import { cx } from "../lib/cx";

/**
 * 폼 컨트롤 — 원본 `app.css` 의 `.field` / `.field-label` / `.input` / `.select` / `.checkbox`.
 *
 * 포커스 시 배경을 틴트로 바꾸는 것(`focus:bg-blue-025`)이 사내 관례입니다. 여기서 기본 outline 을
 * 끄는 대신 전역 `:focus-visible` 링(`src/styles/index.css`)이 키보드 사용자에게 링을 보여줍니다 —
 * 마우스 클릭에는 링이 없고 키보드 탭에는 있습니다.
 */

const CONTROL =
  "h-[40px] rounded-xl border border-grey-200 bg-white px-[12px] font-sans text-[14px] " +
  "font-medium text-grey-900 focus:border-blue-500 focus:bg-blue-025 focus:outline-none";

/**
 * select 전용 — 브라우저 기본 화살표는 테두리·배경을 지정하면 안쪽 여백 없이 오른쪽 가장자리에 붙으므로
 * `appearance-none` 으로 끄고 12px SVG 화살표를 오른쪽 10px 안쪽에 직접 그립니다(우측 패딩 30px 확보).
 * 펼친 목록은 OS 네이티브 팝업이라 `option` 색을 토큰으로 지정합니다.
 */
const SELECT_ARROW =
  "appearance-none pr-[30px] bg-no-repeat bg-[right_10px_center] bg-[length:12px_12px] " +
  "bg-[url(data:image/svg+xml,%3Csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%2212%22%20height=%2212%22%20viewBox=%220%200%2012%2012%22%3E%3Cpath%20d=%22M2%204.5l4%204%204-4%22%20fill=%22none%22%20stroke=%22%23666%22%20stroke-width=%221.6%22%20stroke-linecap=%22round%22%20stroke-linejoin=%22round%22/%3E%3C/svg%3E)] " +
  "disabled:cursor-not-allowed disabled:opacity-60 [&>option]:bg-white [&>option]:text-grey-900";

/** 라벨 + 컨트롤 묶음. `htmlFor` 와 컨트롤의 `id` 를 반드시 짝지어 호출하세요. */
export function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-[6px]">
      <label className="text-[12px] font-bold text-grey-600" htmlFor={id}>
        {label}
      </label>
      {children}
    </div>
  );
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx(CONTROL, className)} {...rest} />;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cx(CONTROL, "h-auto min-h-[80px] resize-y py-[10px] leading-[1.5]", className)}
      {...rest}
    />
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "children"> {
  options: SelectOption[];
}

export function Select({ options, className, ...rest }: SelectProps) {
  return (
    <select className={cx(CONTROL, SELECT_ARROW, className)} {...rest}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** 체크박스 + 라벨 한 줄. 라벨이 컨트롤을 감싸므로 문구 클릭으로도 토글됩니다. */
export function Checkbox({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      className="inline-flex items-center gap-[8px] text-[14px] font-semibold text-grey-700"
      htmlFor={id}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(ev) => onChange(ev.target.checked)}
      />
      {label}
    </label>
  );
}
